import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Grid, TextField, Typography, Alert, Box, CircularProgress, InputAdornment,
  Card, CardMedia, CardActionArea, FormControl, InputLabel, Select, MenuItem,
  Chip, Button, Badge, IconButton
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import SortIcon from '@mui/icons-material/Sort';
import ClearIcon from '@mui/icons-material/Clear';
import RecipeCard from "../components/RecipeCard";
import PantryMatcherDialog from "../components/PantryMatcherDialog";

// Standardization & Seed Lists
import { SEED_INGREDIENTS } from "../utils/recipeStandardization";

// FIREBASE IMPORTS
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const QUICK_FILTERS = [
  { id: "all", label: "All Recipes" },
  { id: "quick", label: "⚡ Quick (< 30 min)" },
  { id: "high_rated", label: "⭐ Top Rated (4.5+)" },
  { id: "vegetarian", label: "🌱 Vegetarian" }
];

const Recipes = () => {
  // DATA STATES
  const [allRecipes, setAllRecipes] = useState([]);
  const [featuredRecipes, setFeaturedRecipes] = useState([]);
  
  // UI & FILTER STATES
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCuisine, setSelectedCuisine] = useState("All");
  const [selectedTag, setSelectedTag] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  // Pantry Matcher States
  const [pantryOpen, setPantryOpen] = useState(false);
  const [pantryItems, setPantryItems] = useState([]);
  
  const navigate = useNavigate();
  const carouselRef = useRef(null);

  const normalizeRecipe = (data, id, favoriteIds) => {
    let rawAuthor = data.authorName || data.username || "Chef";
    if (rawAuthor.includes("@")) {
      rawAuthor = rawAuthor.split("@")[0];
    }
    return {
      ...data,
      id: String(id),
      recipe_name: data.title || data.recipe_name || "Untitled", 
      username: rawAuthor,
      image: (data.images && data.images.length > 0 && data.images[0]) ? data.images[0] : (data.image || "/no-image.png"),
      prep_time: data.prepTime || data.prep_time || 0,
      cook_time: data.cookTime || data.cook_time || 0,
      difficulty: data.difficulty || "Medium",
      rating: data.rating || 0,
      rating_count: data.rating_count || 0,
      cuisine: data.cuisine || "General",
      ingredients: data.ingredients || [],
      tags: data.tags || [],
      is_favorited: favoriteIds.has(String(id)),
      is_featured: data.is_featured || false
    };
  };

  // 1. Fetch Everything Once
  useEffect(() => {
    const auth = getAuth();
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoading(true);
      try {
        let favoriteIds = new Set();
        
        // Fetch User Favorites from their Firestore document
        if (user) {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const rawFavs = userDoc.data().favoriteIds || [];
            rawFavs.forEach(id => favoriteIds.add(String(id)));
          }
        }

        // Fetch All Recipes
        const querySnapshot = await getDocs(collection(db, "recipes"));
        const fetchedRecipes = querySnapshot.docs.map(docSnap => 
          normalizeRecipe(docSnap.data(), docSnap.id, favoriteIds)
        );
        
        setAllRecipes(fetchedRecipes);
        setFeaturedRecipes(fetchedRecipes.filter(r => r.is_featured));

      } catch (err) {
        console.error("Fetch Error:", err);
        setError("Failed to load recipes.");
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // 2. Carousel Auto-Scroll for Featured
  useEffect(() => {
    const scrollContainer = carouselRef.current;
    if (!scrollContainer || featuredRecipes.length < 2) return;
    const scrollAmount = 320; 
    const autoScroll = setInterval(() => {
      if (scrollContainer) {
        const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        if (scrollContainer.scrollLeft >= maxScroll - 10) { 
            scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
            scrollContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      }
    }, 4000); 
    return () => clearInterval(autoScroll);
  }, [featuredRecipes]);

  // Extract all unique ingredients present across all recipes for the pantry matcher
  const allKnownIngredients = useMemo(() => {
    const set = new Set(SEED_INGREDIENTS);
    allRecipes.forEach(r => {
      (r.ingredients || []).forEach(ing => {
        if (ing.ingredient_name) set.add(ing.ingredient_name.trim());
      });
    });
    return Array.from(set).sort();
  }, [allRecipes]);

  // Dynamic list of cuisines present in actual recipes (no blank standard lists)
  const presentCuisines = useMemo(() => {
    const set = new Set();
    allRecipes.forEach(r => {
      if (r.cuisine && r.cuisine.trim()) set.add(r.cuisine.trim());
    });
    return ["All", ...Array.from(set).sort()];
  }, [allRecipes]);

  // 3. Deep Live Search, Multi-Filter, and Multi-Sort Pipeline
  const displayedRecipes = useMemo(() => {
    let result = [...allRecipes];

    // --- A. Text Search (title, author, description, cuisine, tags, and ingredient names) ---
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase().trim();
      result = result.filter(r => {
        const matchesName = r.recipe_name && r.recipe_name.toLowerCase().includes(lowerQuery);
        const matchesUser = r.username && r.username.toLowerCase().includes(lowerQuery);
        const matchesDesc = r.description && r.description.toLowerCase().includes(lowerQuery);
        const matchesCuisine = r.cuisine && r.cuisine.toLowerCase().includes(lowerQuery);
        const matchesTags = r.tags && r.tags.some(t => t.toLowerCase().includes(lowerQuery));
        const matchesIngredients = r.ingredients && r.ingredients.some(ing => 
          (ing.ingredient_name && ing.ingredient_name.toLowerCase().includes(lowerQuery))
        );
        return matchesName || matchesUser || matchesDesc || matchesCuisine || matchesTags || matchesIngredients;
      });
    }

    // --- B. Cuisine Filter ---
    if (selectedCuisine && selectedCuisine !== "All") {
      result = result.filter(r => (r.cuisine || "").toLowerCase() === selectedCuisine.toLowerCase());
    }

    // --- C. Quick Tag Filter ---
    if (selectedTag === "quick") {
      result = result.filter(r => {
        const total = (Number(r.prep_time || 0) + Number(r.cook_time || 0));
        return total > 0 && total <= 30;
      });
    } else if (selectedTag === "high_rated") {
      result = result.filter(r => Number(r.rating || 0) >= 4.5);
    } else if (selectedTag === "vegetarian") {
      result = result.filter(r => 
        (r.tags && r.tags.some(t => t.toLowerCase().includes("veg"))) ||
        (r.cuisine || "").toLowerCase().includes("veg")
      );
    }

    // --- D. Pantry Matcher Score & Ranking ---
    if (pantryItems.length > 0) {
      const lowerPantry = pantryItems.map(p => p.toLowerCase().trim());
      result = result.map(recipe => {
        const recipeIngs = (recipe.ingredients || []).map(i => (i.ingredient_name || "").toLowerCase().trim());
        const matchCount = lowerPantry.filter(p => recipeIngs.some(ri => ri.includes(p) || p.includes(ri))).length;
        const matchPct = recipeIngs.length > 0 ? Math.round((matchCount / recipeIngs.length) * 100) : 0;
        return {
          ...recipe,
          pantryMatchCount: matchCount,
          pantryMatchPct: matchPct
        };
      });

      // Filter recipes that have at least 1 ingredient match
      result = result.filter(r => r.pantryMatchCount > 0);
      result.sort((a, b) => b.pantryMatchCount - a.pantryMatchCount || b.pantryMatchPct - a.pantryMatchPct);
      return result;
    }

    // --- E. Standard Sorting ---
    if (sortBy === "rating") {
      result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    } else if (sortBy === "quickest") {
      result.sort((a, b) => {
        const timeA = (Number(a.prep_time || 0) + Number(a.cook_time || 0)) || 999;
        const timeB = (Number(b.prep_time || 0) + Number(b.cook_time || 0)) || 999;
        return timeA - timeB;
      });
    } else if (sortBy === "alphabetical") {
      result.sort((a, b) => (a.recipe_name || "").localeCompare(b.recipe_name || ""));
    } else {
      // Default: Newest first
      result.sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
    }

    return result;
  }, [allRecipes, searchQuery, selectedCuisine, selectedTag, sortBy, pantryItems]);

  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedCuisine("All");
    setSelectedTag("all");
    setSortBy("newest");
    setPantryItems([]);
  };

  const isFiltered = searchQuery || selectedCuisine !== "All" || selectedTag !== "all" || pantryItems.length > 0;

  return (
    <Container maxWidth="lg" sx={{ pb: 8 }}>
      
      {/* Featured Section (Visible when no filters are active) */}
      {featuredRecipes.length > 0 && !isFiltered && (
        <Box sx={{ mb: 5, mt: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ fontFamily: "'Patrick Hand', cursive", color: '#FF7043', display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon sx={{ mb: 0.5 }} /> Featured Favorites
            </Typography>
            <Box ref={carouselRef} sx={{ display: 'flex', gap: 3, overflowX: 'auto', pb: 2, scrollBehavior: 'smooth' }}>
                {featuredRecipes.map((recipe) => (
                    <Box key={recipe.id} sx={{ minWidth: { xs: '85vw', sm: '350px' }, flexShrink: 0 }}>
                        <Card sx={{ height: '100%', borderRadius: 3 }}>
                            <CardActionArea onClick={() => navigate(`/recipes/${recipe.id}`)}>
                                <CardMedia component="img" height="200" image={recipe.image} />
                                <Box sx={{ position: 'absolute', bottom: 0, width: '100%', bgcolor: 'rgba(0,0,0,0.6)', color: 'white', p: 2 }}>
                                    <Typography variant="h6">{recipe.recipe_name}</Typography>
                                    <Typography variant="caption">by {recipe.username}</Typography>
                                </Box>
                            </CardActionArea>
                        </Card>
                    </Box>
                ))}
            </Box>
        </Box>
      )}

      {/* Header & Main Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontFamily: "'Patrick Hand', cursive", color: '#477491' }}>
          All Recipes
        </Typography>

        {/* Pantry Matcher Trigger Button */}
        <Badge badgeContent={pantryItems.length} color="warning">
          <Button
            variant={pantryItems.length > 0 ? "contained" : "outlined"}
            startIcon={<SoupKitchenIcon />}
            onClick={() => setPantryOpen(true)}
            sx={{
              borderRadius: 3,
              fontWeight: 'bold',
              bgcolor: pantryItems.length > 0 ? '#D28415' : 'transparent',
              color: pantryItems.length > 0 ? '#fff' : '#D28415',
              borderColor: '#D28415',
              '&:hover': { bgcolor: pantryItems.length > 0 ? '#b36b10' : 'rgba(210, 132, 21, 0.08)' }
            }}
          >
            {pantryItems.length > 0 ? `Pantry (${pantryItems.length})` : "What Can I Make?"}
          </Button>
        </Badge>
      </Box>

      {/* Search Bar & Unified Filter Controls Row */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          {/* Search Box */}
          <Grid item xs={12} md={5}>
            <TextField 
              placeholder="Search recipes, ingredients, chefs..." 
              fullWidth 
              size="small"
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              InputProps={{ 
                startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }} 
            />
          </Grid>

          {/* Cuisine Select Dropdown */}
          <Grid item xs={6} sm={4} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="cuisine-filter-label">Cuisine</InputLabel>
              <Select
                labelId="cuisine-filter-label"
                value={selectedCuisine}
                label="Cuisine"
                onChange={(e) => setSelectedCuisine(e.target.value)}
              >
                <MenuItem value="All">All Cuisines</MenuItem>
                {presentCuisines.filter(c => c !== "All").map(c => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Tag / Category Filter Dropdown */}
          <Grid item xs={6} sm={4} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="tag-filter-label">Diet & Tags</InputLabel>
              <Select
                labelId="tag-filter-label"
                value={selectedTag}
                label="Diet & Tags"
                onChange={(e) => setSelectedTag(e.target.value)}
              >
                {QUICK_FILTERS.map(qf => (
                  <MenuItem key={qf.id} value={qf.id}>{qf.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Sort By Dropdown */}
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="sort-by-label">Sort By</InputLabel>
              <Select
                labelId="sort-by-label"
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
                startAdornment={<InputAdornment position="start"><SortIcon fontSize="small" /></InputAdornment>}
              >
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="rating">Highest Rated</MenuItem>
                <MenuItem value="quickest">Quickest Time</MenuItem>
                <MenuItem value="alphabetical">Title (A - Z)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Active Filter Indicators (Only displays active filters cleanly) */}
      {(selectedCuisine !== "All" || selectedTag !== "all" || searchQuery) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>
            Active:
          </Typography>
          {selectedCuisine !== "All" && (
            <Chip
              size="small"
              label={`Cuisine: ${selectedCuisine}`}
              onDelete={() => setSelectedCuisine("All")}
              color="warning"
              variant="outlined"
            />
          )}
          {selectedTag !== "all" && (
            <Chip
              size="small"
              label={QUICK_FILTERS.find(q => q.id === selectedTag)?.label || selectedTag}
              onDelete={() => setSelectedTag("all")}
              color="primary"
              variant="outlined"
            />
          )}
          {searchQuery && (
            <Chip
              size="small"
              label={`"${searchQuery}"`}
              onDelete={() => setSearchQuery("")}
              variant="outlined"
            />
          )}
          <Button size="small" onClick={handleResetFilters} sx={{ textTransform: 'none', py: 0, fontSize: '0.75rem' }}>
            Clear All
          </Button>
        </Box>
      )}

      {/* Active Pantry Matcher Banner */}
      {pantryItems.length > 0 && (
        <Alert 
          severity="info" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setPantryItems([])}>
              Clear Pantry
            </Button>
          }
        >
          <strong>Pantry Matcher Active:</strong> Showing recipes matching your ingredients ({pantryItems.join(", ")}).
        </Alert>
      )}

      {/* Recipe Count & Active Filter Reset */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Showing <strong>{displayedRecipes.length}</strong> {displayedRecipes.length === 1 ? "recipe" : "recipes"}
        </Typography>
        {isFiltered && (
          <Button size="small" color="inherit" onClick={handleResetFilters} startIcon={<ClearIcon />}>
            Reset All Filters
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2.5}>
            {displayedRecipes.length > 0 ? displayedRecipes.map((recipe) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={recipe.id} sx={{ display: 'flex' }}>
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <RecipeCard recipe={recipe} />
                      {/* Pantry Match Indicator Badge */}
                      {recipe.pantryMatchCount > 0 && (
                        <Box sx={{ mt: 1, px: 1, py: 0.5, bgcolor: '#e8f5e9', borderRadius: 1.5, display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
                          <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                            ✓ {recipe.pantryMatchCount} pantry {recipe.pantryMatchCount === 1 ? "match" : "matches"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                            {recipe.pantryMatchPct}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                </Grid>
            )) : (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#faf7f2', borderRadius: 3 }}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      No recipes found matching your criteria.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Try adjusting your search terms, clearing cuisine filters, or removing pantry ingredients.
                    </Typography>
                    {isFiltered && (
                      <Button variant="outlined" onClick={handleResetFilters} sx={{ borderColor: '#D28415', color: '#D28415' }}>
                        Reset All Filters
                      </Button>
                    )}
                  </Box>
                </Grid>
            )}
        </Grid>
      )}

      {/* Pantry Matcher Dialog */}
      <PantryMatcherDialog
        open={pantryOpen}
        onClose={() => setPantryOpen(false)}
        pantryItems={pantryItems}
        onPantryItemsChange={setPantryItems}
        availableIngredients={allKnownIngredients}
      />
    </Container>
  );
};

export default Recipes;
